import React, { useState, useEffect } from 'react';
import { Save, Send, AlertCircle, CheckCircle } from 'lucide-react';
import ApiService from '../../services/api';

interface TelegramSettings {
  botToken: string;
  chatId: string;
  isActive: boolean;
  sendNewAppointments: boolean;
  sendStatusUpdates: boolean;
}

const TelegramSettingsComponent: React.FC = () => {
  const [settings, setSettings] = useState<TelegramSettings>({
    botToken: '',
    chatId: '',
    isActive: false,
    sendNewAppointments: true,
    sendStatusUpdates: true
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [homeSettings, setHomeSettings] = useState<any>(null);

  useEffect(() => {
    loadSettings();
    
    // Home settings yükle
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response.settings);
      } catch (error) {
        console.error('Home settings yüklenemedi:', error);
      }
    };
    loadHomeSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getTelegramSettings();
      if (response && response.settings) {
        setSettings(response.settings);
      }
    } catch (error) {
      console.error('Telegram ayarları yüklenirken hata oluştu:', error);
      setMessage({ type: 'error', text: 'Ayarlar yüklenirken hata oluştu.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const response = await ApiService.saveTelegramSettings(settings);
      
      if (response && response.success) {
        setMessage({ type: 'success', text: 'Telegram ayarları başarıyla kaydedildi!' });
      } else {
        setMessage({ type: 'error', text: response?.message || 'Ayarlar kaydedilirken hata oluştu.' });
      }
    } catch (error: any) {
      console.error('Telegram ayarları kaydedilirken hata oluştu:', error);
      setMessage({ type: 'error', text: error?.message || 'Ayarlar kaydedilirken hata oluştu.' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestMessage = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const response = await ApiService.sendTelegramTestMessage();
      
      if (response && response.success) {
        setMessage({ type: 'success', text: 'Test mesajı başarıyla gönderildi!' });
      } else {
        setMessage({ type: 'error', text: response?.message || 'Test mesajı gönderilirken hata oluştu.' });
      }
    } catch (error: any) {
      console.error('Test mesajı gönderilirken hata oluştu:', error);
      setMessage({ type: 'error', text: error?.message || 'Test mesajı gönderilirken hata oluştu.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof TelegramSettings, value: string | boolean) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading && !settings.botToken) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Telegram Bot Ayarları</h2>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center space-x-3 ${
          message.type === 'success' 
            ? 'bg-green-900 text-green-200 border border-green-700' 
            : 'bg-red-900 text-red-200 border border-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Bot Ayarları */}
      <div className="rounded-lg shadow-sm p-6 text-white border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <h3 className="text-lg font-semibold text-white mb-4">Bot Ayarları</h3>
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Bot Token
            </label>
            <input
              type="text"
              value={settings.botToken}
              onChange={(e) => handleInputChange('botToken', e.target.value)}
              placeholder="Bot token'ınızı girin"
              className="w-full px-3 py-2 border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
              style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Chat ID (Grup ID)
            </label>
            <input
              type="text"
              value={settings.chatId}
              onChange={(e) => handleInputChange('chatId', e.target.value)}
              placeholder="Chat ID'nizi girin"
              className="w-full px-3 py-2 border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
              style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
            />
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="isActive"
              checked={settings.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              className="h-4 w-4 text-pink-600 border-gray-600 rounded focus:ring-pink-500"
              style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-300">
              Telegram Bot'u Aktif Et
            </label>
          </div>
        </div>
      </div>

      {/* Bildirim Ayarları */}
      <div className="rounded-lg shadow-sm p-6 text-white border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <h3 className="text-lg font-semibold text-white mb-4">Bildirim Ayarları</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="sendNewAppointments"
              checked={settings.sendNewAppointments}
              onChange={(e) => handleInputChange('sendNewAppointments', e.target.checked)}
              className="h-4 w-4 text-pink-600 border-gray-600 rounded focus:ring-pink-500"
              style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
            />
            <label htmlFor="sendNewAppointments" className="text-sm font-medium text-gray-300">
              Yeni randevu bildirimleri gönder
            </label>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="sendStatusUpdates"
              checked={settings.sendStatusUpdates}
              onChange={(e) => handleInputChange('sendStatusUpdates', e.target.checked)}
              className="h-4 w-4 text-pink-600 border-gray-600 rounded focus:ring-pink-500"
              style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
            />
            <label htmlFor="sendStatusUpdates" className="text-sm font-medium text-gray-300">
              Randevu durumu güncellemeleri gönder
            </label>
          </div>
        </div>
      </div>

      {/* Kaydet ve Test Butonları */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center justify-center space-x-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200"
        >
          <Save className="w-4 h-4" />
          <span>{loading ? 'Kaydediliyor...' : 'Ayarları Kaydet'}</span>
        </button>

        <button
          onClick={handleTestMessage}
          disabled={loading || !settings.botToken || !settings.chatId}
          className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200"
        >
          <Send className="w-4 h-4" />
          <span>{loading ? 'Gönderiliyor...' : 'Test Mesajı Gönder'}</span>
        </button>
      </div>


    </div>
  );
};

export default TelegramSettingsComponent;
